/**
 * @zendir/ui - AppBar Component
 *
 * Enterprise-grade application bar with integrated theme controls,
 * compact mode toggle, and display settings management.
 *
 * Features:
 * - Customizable branding (logo, name, domain, version)
 * - Dark/light mode toggle
 * - Theme variant selector
 * - Color picker (accent + glass tint)
 * - Compact mode toggle (affects all cards globally)
 * - Integrates with DisplaySettingsProvider
 * - Responsive design
 * - AstroUXDS compliant
 *
 * @example
 * ```tsx
 * import { AppBar, ThemeProvider, DisplaySettingsProvider } from '@zendir/ui';
 *
 * <ThemeProvider>
 *   <DisplaySettingsProvider>
 *     <AppBar
 *       appName="Mission Control"
 *       appDomain="NASA"
 *       appVersion="1.0.0"
 *       showThemeControls
 *       showCompactToggle
 *     />
 *     <App />
 *   </DisplaySettingsProvider>
 * </ThemeProvider>
 * ```
 */

import React, {
  memo,
  useMemo,
  useState,
  useRef,
  useEffect,
  type ReactNode,
} from "react";
import { useTheme, type ThemeVariant, type ThemeMode } from "../../theme";
import { useDisplaySettingsOptional } from "../../context/DisplaySettingsContext";
import { classNames, safeAccentText } from "../../utils";
import { AstroIcon } from "../display/AstroIcon";
import { ColorPickerPanel } from "../widgets/ColorPickerPanel";
import { Tooltip } from "../overlays/Tooltip";
import { useBreakpoint } from "../layout/useBreakpoint";

// =============================================================================
// Types
// =============================================================================

export interface AppBarBranding {
  /** Logo image URL (takes precedence over text) */
  logoUrl?: string;
  /** Logo as React node (takes precedence over logoUrl) */
  logo?: ReactNode;
  /** Logo alt text for accessibility */
  logoAlt?: string;
  /** Logo height in pixels (default: 28) */
  logoHeight?: number;
  /** Link URL when clicking on branding */
  href?: string;
}

export interface AppBarProps {
  /** Application name */
  appName?: string;
  /** Application domain/organization (shown above app name) */
  appDomain?: string;
  /** Application version (shown smaller) */
  appVersion?: string;
  /** Logo/branding configuration */
  branding?: AppBarBranding;

  // Control visibility
  /** Show theme controls (mode toggle, theme selector, color picker) */
  showThemeControls?: boolean;
  /** Show compact mode toggle */
  showCompactToggle?: boolean;
  /** Show color picker (only visible for transparent themes) */
  showColorPicker?: boolean;
  /** Show theme variant selector */
  showThemeSelector?: boolean;
  /** Show only mode toggle (hide other theme controls) */
  showModeOnly?: boolean;
  /** Use compact dropdown for theme controls instead of buttons */
  compactThemeControls?: boolean;

  // Default values (for when controls are hidden)
  /** Default theme to apply if not already set */
  defaultTheme?: ThemeVariant;
  /** Default mode to apply if not already set */
  defaultMode?: ThemeMode;
  /** Default compact mode */
  defaultCompactMode?: boolean;

  // Slots
  /** Left slot content (after branding, or only content when showBranding is false) */
  leftSlot?: ReactNode;
  /** Center slot content */
  centerSlot?: ReactNode;
  /** Right slot content (before theme controls) */
  rightSlot?: ReactNode;
  /** Optional title shown above right slot (e.g. "Data" for metrics/refresh section) */
  rightSectionTitle?: string;

  // Layout (operator / mission-control style)
  /** Show branding block (app name, logo). Set false for control-only bars. */
  showBranding?: boolean;
  /** Let center slot grow to fill space and stay centered (e.g. simulation controls). */
  centerGrow?: boolean;
  /** Bar variant: default (52px) or dense (44px min height, tighter padding/gap). */
  variant?: "default" | "dense";

  // Callbacks
  /** Callback when theme changes */
  onThemeChange?: (theme: ThemeVariant) => void;
  /** Callback when mode changes */
  onModeChange?: (mode: ThemeMode) => void;
  /** Callback when compact mode changes */
  onCompactModeChange?: (compact: boolean) => void;

  // Styling
  /** Custom className */
  className?: string;
  /** Custom height (default: 52) */
  height?: number;
  /** Sticky positioning */
  sticky?: boolean;
}

// =============================================================================
// Theme Variants for Selector
// =============================================================================

const THEME_VARIANTS: { key: ThemeVariant; label: string; tooltip: string }[] =
  [
    {
      key: "hybrid",
      label: "Zen (Hybrid)",
      tooltip: "Default theme - Astro status colors with purple accents",
    },
    {
      key: "purple-hue",
      label: "Zen (Purple Hue)",
      tooltip: "Purple accent theme - vibrant purple tones",
    },
    {
      key: "astro",
      label: "Astro",
      tooltip: "Astro UX Design System theme - dark blue tones",
    },
    {
      key: "transparent",
      label: "Glass",
      tooltip: "Glassmorphism theme - transparent with blur effects",
    },
    {
      key: "transparent-bold",
      label: "Bold",
      tooltip: "Bold glass theme - stronger transparency effects",
    },
    {
      key: "transparent-minimal",
      label: "Minimal",
      tooltip: "Minimal glass theme - ultra-subtle borders, clean surfaces",
    },
  ];

// =============================================================================
// Mode Toggle Component
// =============================================================================

interface ModeToggleProps {
  mode: ThemeMode;
  onToggle: () => void;
  tokens: any;
}

const ModeToggle = memo(function ModeToggle({
  mode,
  onToggle,
  tokens,
}: ModeToggleProps) {
  const isDark = mode === "dark";
  const tooltipText = isDark ? "Switch to Light Mode" : "Switch to Dark Mode";

  return (
    <Tooltip content={tooltipText} placement="bottom">
      <button
        onClick={onToggle}
        aria-label={tooltipText}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          padding: 0,
          border: "none",
          borderRadius: tokens.borderRadius.md,
          backgroundColor: "rgba(255,255,255,0.08)",
          color: tokens.colors.text.primary,
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
        }}
      >
        <AstroIcon
          name={isDark ? "brightness-3" : "brightness-high"}
          size={16}
          color={tokens.colors.text.primary}
        />
      </button>
    </Tooltip>
  );
});

// =============================================================================
// Compact Mode Toggle Component
// =============================================================================

interface CompactToggleProps {
  isCompact: boolean;
  onToggle: () => void;
  tokens: any;
}

const CompactToggle = memo(function CompactToggle({
  isCompact,
  onToggle,
  tokens,
}: CompactToggleProps) {
  const tooltipText = isCompact
    ? "Switch to Expanded View"
    : "Switch to Compact View";

  return (
    <Tooltip content={tooltipText} placement="bottom">
      <button
        onClick={onToggle}
        aria-label={tooltipText}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          padding: 0,
          border: "none",
          borderRadius: tokens.borderRadius.md,
          backgroundColor: isCompact
            ? `${tokens.colors.accent.primary}40`
            : "rgba(255,255,255,0.08)",
          color: isCompact
            ? tokens.colors.accent.secondary
            : tokens.colors.text.primary,
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isCompact
            ? `${tokens.colors.accent.primary}59`
            : "rgba(255,255,255,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isCompact
            ? `${tokens.colors.accent.primary}40`
            : "rgba(255,255,255,0.08)";
        }}
      >
        <AstroIcon
          name={isCompact ? "view-comfy" : "view-module"}
          size={16}
          color={
            isCompact
              ? tokens.colors.accent.secondary
              : tokens.colors.text.primary
          }
        />
      </button>
    </Tooltip>
  );
});

// =============================================================================
// Theme Selector Component
// =============================================================================

interface ThemeSelectorProps {
  currentTheme: ThemeVariant;
  onSelect: (theme: ThemeVariant) => void;
  tokens: any;
}

const ThemeSelector = memo(function ThemeSelector({
  currentTheme,
  onSelect,
  tokens,
}: ThemeSelectorProps) {
  // WCAG AA: compute a safe background for selected theme buttons (white text)
  const safeAccentBg = useMemo(() => {
    const hex = tokens.colors.accent.primary.replace("#", "");
    if (hex.length < 6) return tokens.colors.accent.primary;
    const toLinear = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const L =
      0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    if (1.05 / (L + 0.05) >= 4.5) return tokens.colors.accent.primary;
    const factor = 0.88;
    const dr = Math.min(255, Math.round(r * 255 * factor));
    const dg = Math.min(255, Math.round(g * 255 * factor));
    const db = Math.min(255, Math.round(b * 255 * factor));
    return `#${dr.toString(16).padStart(2, "0")}${dg.toString(16).padStart(2, "0")}${db.toString(16).padStart(2, "0")}`;
  }, [tokens.colors.accent.primary]);

  // Group themes for better organization
  const coreThemes = THEME_VARIANTS.filter(
    (t) => !t.key.startsWith("transparent"),
  );
  const glassThemes = THEME_VARIANTS.filter((t) =>
    t.key.startsWith("transparent"),
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: 2,
        backgroundColor: "rgba(255,255,255,0.05)",
        borderRadius: tokens.borderRadius.md,
      }}
      role="group"
      aria-label="Theme selector"
    >
      {/* Core themes */}
      {coreThemes.map(({ key, label, tooltip }) => (
        <Tooltip key={key} content={tooltip} placement="bottom">
          <button
            onClick={() => onSelect(key)}
            aria-label={`Select ${label} theme`}
            style={{
              padding: "4px 10px",
              border: "none",
              borderRadius: tokens.borderRadius.sm,
              backgroundColor:
                currentTheme === key ? safeAccentBg : "transparent",
              color:
                currentTheme === key ? "#ffffff" : tokens.colors.text.secondary,
              fontSize: "0.6875rem", // 11px in rem
              fontWeight: currentTheme === key ? 500 : 400, // AstroUXDS: 500 (medium) not 600
              cursor: "pointer",
              transition: "all 0.15s ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (currentTheme !== key) {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                currentTheme === key ? safeAccentBg : "transparent";
            }}
          >
            {label}
          </button>
        </Tooltip>
      ))}

      {/* Separator */}
      <div
        style={{
          width: 1,
          height: 16,
          backgroundColor: "rgba(255,255,255,0.15)",
          margin: "0 2px",
        }}
      />

      {/* Glass themes */}
      {glassThemes.map(({ key, label, tooltip }) => (
        <Tooltip key={key} content={tooltip} placement="bottom">
          <button
            onClick={() => onSelect(key)}
            aria-label={`Select ${label} theme`}
            style={{
              padding: "4px 10px",
              border: "none",
              borderRadius: tokens.borderRadius.sm,
              backgroundColor:
                currentTheme === key ? safeAccentBg : "transparent",
              color:
                currentTheme === key ? "#ffffff" : tokens.colors.text.secondary,
              fontSize: "0.6875rem", // 11px in rem
              fontWeight: currentTheme === key ? 500 : 400, // AstroUXDS: 500 (medium) not 600
              cursor: "pointer",
              transition: "all 0.15s ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (currentTheme !== key) {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                currentTheme === key ? safeAccentBg : "transparent";
            }}
          >
            {label}
          </button>
        </Tooltip>
      ))}
    </div>
  );
});

// =============================================================================
// Accent Borders Toggle Component
// =============================================================================

interface AccentBordersToggleProps {
  enabled: boolean;
  onToggle: () => void;
  tokens: any;
}

const AccentBordersToggle = memo(function AccentBordersToggle({
  enabled,
  onToggle,
  tokens,
}: AccentBordersToggleProps) {
  return (
    <Tooltip
      content={
        enabled
          ? "Accent borders enabled - Click to disable"
          : "Accent borders disabled - Click to enable"
      }
      placement="bottom"
    >
      <button
        onClick={onToggle}
        aria-label={
          enabled ? "Disable accent borders" : "Enable accent borders"
        }
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          padding: "4px 8px",
          border: "none",
          borderRadius: tokens.borderRadius.sm,
          backgroundColor: enabled
            ? `${tokens.colors.accent.primary}30`
            : "rgba(255,255,255,0.05)",
          color: enabled
            ? safeAccentText(tokens.colors.accent.primary)
            : tokens.colors.text.tertiary,
          fontSize: "0.625rem", // 10px in rem
          fontWeight: 500,
          cursor: "pointer",
          transition: "all 0.15s ease",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = enabled
            ? `${tokens.colors.accent.primary}40`
            : "rgba(255,255,255,0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = enabled
            ? `${tokens.colors.accent.primary}30`
            : "rgba(255,255,255,0.05)";
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            border: `2px solid ${enabled ? tokens.colors.accent.primary : tokens.colors.text.tertiary}`,
            backgroundColor: "transparent",
          }}
        />
        <span>Borders</span>
      </button>
    </Tooltip>
  );
});

// =============================================================================
// Theme Controls Dropdown (Compact)
// =============================================================================

interface ThemeControlsDropdownProps {
  currentTheme: ThemeVariant;
  mode: ThemeMode;
  onThemeSelect: (theme: ThemeVariant) => void;
  onModeToggle: () => void;
  accentBordersEnabled?: boolean;
  onAccentBordersToggle?: () => void;
  showAccentBorders?: boolean;
  tokens: any;
}

const ThemeControlsDropdown = memo(function ThemeControlsDropdown({
  currentTheme,
  mode,
  onThemeSelect,
  onModeToggle,
  accentBordersEnabled = true,
  onAccentBordersToggle,
  showAccentBorders = false,
  tokens,
}: ThemeControlsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Get current theme label
  const currentLabel =
    THEME_VARIANTS.find((t) => t.key === currentTheme)?.label || "Theme";

  // Group themes
  const coreThemes = THEME_VARIANTS.filter(
    (t) => !t.key.startsWith("transparent"),
  );
  const glassThemes = THEME_VARIANTS.filter((t) =>
    t.key.startsWith("transparent"),
  );

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Trigger Button */}
      <Tooltip content="Theme settings" placement="bottom">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Theme settings"
          aria-expanded={isOpen}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: isOpen
              ? tokens.colors.background.elevated
              : "transparent",
            color: tokens.colors.text.secondary,
            fontSize: "0.6875rem", // 11px in rem
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (!isOpen) {
              e.currentTarget.style.backgroundColor =
                tokens.colors.background.elevated;
            }
          }}
          onMouseLeave={(e) => {
            if (!isOpen) {
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          <AstroIcon
            name="settings"
            size={14}
            color={tokens.colors.text.secondary}
          />
          <span>{currentLabel}</span>
          <AstroIcon
            name={isOpen ? "arrow-up" : "arrow-down"}
            size={10}
            color={tokens.colors.text.tertiary}
          />
        </button>
      </Tooltip>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            minWidth: 220,
            padding: tokens.spacing.sm,
            backgroundColor: tokens.colors.background.surface,
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.lg,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            zIndex: 1000,
          }}
        >
          {/* Theme Section */}
          <div style={{ marginBottom: tokens.spacing.sm }}>
            <div
              style={{
                fontSize: "0.625rem", // 10px in rem
                color: tokens.colors.text.muted,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 8,
                paddingLeft: 4,
              }}
            >
              Theme
            </div>

            {/* Core Themes */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {coreThemes.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => {
                    onThemeSelect(key);
                    setIsOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: tokens.borderRadius.sm,
                    backgroundColor:
                      currentTheme === key
                        ? `${tokens.colors.accent.primary}20`
                        : "transparent",
                    color:
                      currentTheme === key
                        ? tokens.colors.accent.primary
                        : tokens.colors.text.primary,
                    fontSize: "0.75rem", // 12px in rem
                    fontWeight: currentTheme === key ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (currentTheme !== key) {
                      e.currentTarget.style.backgroundColor =
                        tokens.colors.background.elevated;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      currentTheme === key
                        ? `${tokens.colors.accent.primary}20`
                        : "transparent";
                  }}
                >
                  {currentTheme === key && (
                    <AstroIcon
                      name="check"
                      size={12}
                      color={tokens.colors.accent.primary}
                    />
                  )}
                  <span style={{ marginLeft: currentTheme === key ? 0 : 20 }}>
                    {label}
                  </span>
                </button>
              ))}
            </div>

            {/* Separator */}
            <div
              style={{
                height: 1,
                backgroundColor: tokens.colors.border.muted,
                margin: `${tokens.spacing.xs} 0`,
              }}
            />

            {/* Glass Themes */}
            <div
              style={{
                fontSize: "0.625rem", // 10px in rem
                color: tokens.colors.text.muted,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 6,
                paddingLeft: 4,
              }}
            >
              Glass
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {glassThemes.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => {
                    onThemeSelect(key);
                    setIsOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: tokens.borderRadius.sm,
                    backgroundColor:
                      currentTheme === key
                        ? `${tokens.colors.accent.primary}20`
                        : "transparent",
                    color:
                      currentTheme === key
                        ? tokens.colors.accent.primary
                        : tokens.colors.text.primary,
                    fontSize: "0.75rem", // 12px in rem
                    fontWeight: currentTheme === key ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (currentTheme !== key) {
                      e.currentTarget.style.backgroundColor =
                        tokens.colors.background.elevated;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      currentTheme === key
                        ? `${tokens.colors.accent.primary}20`
                        : "transparent";
                  }}
                >
                  {currentTheme === key && (
                    <AstroIcon
                      name="check"
                      size={12}
                      color={tokens.colors.accent.primary}
                    />
                  )}
                  <span style={{ marginLeft: currentTheme === key ? 0 : 20 }}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              height: 1,
              backgroundColor: tokens.colors.border.muted,
              margin: `${tokens.spacing.sm} 0`,
            }}
          />

          {/* Options Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Mode Toggle */}
            <button
              onClick={() => {
                onModeToggle();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                border: "none",
                borderRadius: tokens.borderRadius.sm,
                backgroundColor: "transparent",
                color: tokens.colors.text.primary,
                fontSize: "0.75rem", // 12px in rem
                cursor: "pointer",
                transition: "all 0.1s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  tokens.colors.background.elevated;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <AstroIcon
                  name={mode === "dark" ? "brightness-high" : "brightness-low"}
                  size={14}
                  color={tokens.colors.text.secondary}
                />
                {mode === "dark" ? "Dark Mode" : "Light Mode"}
              </span>
              <div
                style={{
                  width: 32,
                  height: 18,
                  borderRadius: 9,
                  backgroundColor:
                    mode === "dark"
                      ? tokens.colors.accent.primary
                      : tokens.colors.border.default,
                  padding: 2,
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    transition: "all 0.15s ease",
                    transform:
                      mode === "dark" ? "translateX(14px)" : "translateX(0)",
                  }}
                />
              </div>
            </button>

            {/* Accent Borders (only for glass themes) */}
            {showAccentBorders && onAccentBordersToggle && (
              <button
                onClick={onAccentBordersToggle}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  border: "none",
                  borderRadius: tokens.borderRadius.sm,
                  backgroundColor: "transparent",
                  color: tokens.colors.text.primary,
                  fontSize: "0.75rem", // 12px in rem
                  cursor: "pointer",
                  transition: "all 0.1s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    tokens.colors.background.elevated;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      border: `2px solid ${tokens.colors.text.secondary}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {accentBordersEnabled && (
                      <AstroIcon
                        name="check"
                        size={10}
                        color={tokens.colors.text.secondary}
                      />
                    )}
                  </span>
                  Accent Borders
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// =============================================================================
// AppBar Component
// =============================================================================

// =============================================================================
// Section header for right slot (operator-style "Data" header)
// =============================================================================

interface SectionHeaderProps {
  title: string;
  tokens: { typography: any; spacing: any; colors: any; borders: any };
}

const SectionHeader = memo(function SectionHeader({
  title,
  tokens,
}: SectionHeaderProps) {
  return (
    <div style={{ textAlign: "center", marginBottom: tokens.spacing.xs }}>
      <span
        style={{
          fontSize: tokens.typography.fontSize.xxs,
          fontFamily: tokens.typography.fontFamily.primary,
          color:
            tokens.colors.interactive?.default ?? tokens.colors.text.secondary,
          fontWeight: tokens.typography.fontWeight.bold,
        }}
      >
        {title}
      </span>
      <div
        style={{
          width: "100%",
          height: tokens.borders.width.thin,
          background:
            tokens.colors.interactive?.default ?? tokens.colors.text.secondary,
          opacity: 0.5,
          marginTop: tokens.spacing.xs,
        }}
      />
    </div>
  );
});

// =============================================================================
// AppBar Component
// =============================================================================

export const AppBar = memo(function AppBar({
  appName = "Zendir",
  appDomain,
  appVersion,
  branding,
  showThemeControls = true,
  showCompactToggle = true,
  showColorPicker = true,
  showThemeSelector = true,
  showModeOnly = false,
  compactThemeControls = false,
  defaultTheme,
  defaultMode,
  defaultCompactMode,
  leftSlot,
  centerSlot,
  rightSlot,
  rightSectionTitle,
  showBranding = true,
  centerGrow = false,
  variant = "default",
  onThemeChange,
  onModeChange,
  onCompactModeChange,
  className = "",
  height = 52,
  sticky = true,
}: AppBarProps): React.ReactElement {
  const { tokens, theme, mode, setTheme, setMode, toggleMode } = useTheme();
  const { isMobile } = useBreakpoint();
  const displaySettings = useDisplaySettingsOptional();

  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";

  // Get compact mode from context or default
  const compactMode =
    displaySettings?.compactMode ?? defaultCompactMode ?? false;

  // Apply defaults on first render if specified
  React.useEffect(() => {
    if (defaultTheme && theme !== defaultTheme) {
      setTheme(defaultTheme);
      onThemeChange?.(defaultTheme);
    }
    if (defaultMode && mode !== defaultMode) {
      setMode(defaultMode);
      onModeChange?.(defaultMode);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle mode toggle
  const handleModeToggle = () => {
    toggleMode();
    onModeChange?.(mode === "dark" ? "light" : "dark");
  };

  // Handle theme change
  const handleThemeChange = (newTheme: ThemeVariant) => {
    setTheme(newTheme);
    onThemeChange?.(newTheme);
  };

  // Handle compact mode toggle
  const handleCompactToggle = () => {
    if (displaySettings) {
      displaySettings.toggleCompactMode();
      onCompactModeChange?.(!compactMode);
    } else {
      onCompactModeChange?.(!compactMode);
    }
  };

  // Variant-driven layout (operator / mission-control style)
  const isDense = variant === "dense";
  const effectiveHeight = height ?? (isDense ? 44 : 52);
  const paddingH = isDense
    ? isMobile
      ? tokens.spacing.xs
      : tokens.spacing.sm
    : isMobile
      ? tokens.spacing.md
      : tokens.spacing.lg;
  const paddingV = isDense ? tokens.spacing.xs : 0;
  const gap = isDense
    ? isMobile
      ? tokens.spacing.sm
      : tokens.spacing.md
    : tokens.spacing.sm;
  const minHeight = isDense && isMobile ? 44 : undefined;

  // Render branding section
  const renderBranding = () => {
    // Custom logo node
    if (branding?.logo) {
      const content = branding.logo;
      if (branding.href) {
        return (
          <a
            href={branding.href}
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            {content}
          </a>
        );
      }
      return <>{content}</>;
    }

    // Logo image URL
    if (branding?.logoUrl) {
      const img = (
        <img
          src={branding.logoUrl}
          alt={branding.logoAlt || appName}
          style={{
            height: branding.logoHeight || 28,
            width: "auto",
            objectFit: "contain",
          }}
        />
      );

      if (branding.href) {
        return (
          <a
            href={branding.href}
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            {img}
          </a>
        );
      }
      return img;
    }

    // Text branding
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: tokens.spacing.sm,
        }}
      >
        {appDomain && (
          <span
            style={{
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: 400,
              color: tokens.colors.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {appDomain}
          </span>
        )}
        <span
          style={{
            fontSize: tokens.typography.fontSize.lg,
            fontWeight: 500, // AstroUXDS medium for app title
            color: tokens.colors.text.primary,
          }}
        >
          {appName}
        </span>
        {appVersion && (
          <span
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.tertiary,
            }}
          >
            v{appVersion.replace(/^v/, "")}
          </span>
        )}
      </div>
    );
  };

  return (
    <header
      className={classNames("zendir-app-bar", className)}
      role="banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: effectiveHeight,
        padding: paddingV ? `${paddingV} ${paddingH}` : `0 ${paddingH}`,
        backgroundColor: isTransparentTheme
          ? "rgba(0,0,0,0.4)"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)" as const,
        }),
        borderBottom: tokens.borders.divider,
        color: tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamily.primary,
        gap,
        ...(minHeight && { minHeight }),
        ...(sticky && {
          position: "sticky",
          top: 0,
          zIndex: 100,
        }),
      }}
    >
      {/* Left section: Branding (optional) + leftSlot */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isDense ? gap : isMobile ? tokens.spacing.md : tokens.spacing.lg,
          flex: "1 1 0",
          minWidth: 0,
        }}
      >
        {showBranding && renderBranding()}
        {leftSlot}
      </div>

      {/* Center section (optionally grows to fill and center content) */}
      {centerSlot && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: centerGrow ? "1 1 0" : "0 0 auto",
            minWidth: 0,
          }}
        >
          {centerSlot}
        </div>
      )}

      {/* Right section: optional section title + rightSlot + theme controls + compact toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: tokens.spacing.sm,
          flex: "1 1 0",
          minWidth: 0,
        }}
      >
        {rightSectionTitle != null && rightSectionTitle !== "" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <SectionHeader title={rightSectionTitle} tokens={tokens} />
            {rightSlot}
          </div>
        ) : (
          rightSlot
        )}

        {/* Theme Controls Group */}
        {showThemeControls && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 6 : 8,
              marginLeft: isMobile ? tokens.spacing.xs : tokens.spacing.sm,
              minWidth: 0,
            }}
          >
            {/* Compact Dropdown Mode */}
            {compactThemeControls ? (
              <ThemeControlsDropdown
                currentTheme={theme}
                mode={mode}
                onThemeSelect={handleThemeChange}
                onModeToggle={handleModeToggle}
                accentBordersEnabled={displaySettings?.accentBorders ?? true}
                onAccentBordersToggle={displaySettings?.toggleAccentBorders}
                showAccentBorders={isTransparentTheme && !!displaySettings}
                tokens={tokens}
              />
            ) : (
              <>
                {/* Theme Selector */}
                {showThemeSelector && !showModeOnly && (
                  <ThemeSelector
                    currentTheme={theme}
                    onSelect={handleThemeChange}
                    tokens={tokens}
                  />
                )}

                {/* Accent Borders Toggle (only for transparent themes) */}
                {!showModeOnly && isTransparentTheme && displaySettings && (
                  <AccentBordersToggle
                    enabled={displaySettings.accentBorders}
                    onToggle={displaySettings.toggleAccentBorders}
                    tokens={tokens}
                  />
                )}

                {/* Color Picker (only for transparent themes) */}
                {showColorPicker &&
                  !showModeOnly &&
                  isTransparentTheme &&
                  displaySettings && (
                    <ColorPickerPanel
                      accentColor={displaySettings.accentColor}
                      glassTint={displaySettings.glassTint}
                      colorTarget={displaySettings.colorTarget}
                      onAccentChange={displaySettings.setAccentColor}
                      onGlassTintChange={displaySettings.setGlassTint}
                      onTargetChange={displaySettings.setColorTarget}
                      onColorSelect={displaySettings.setColor}
                      compact
                    />
                  )}

                {/* Dark/Light Mode Toggle */}
                <ModeToggle
                  mode={mode}
                  onToggle={handleModeToggle}
                  tokens={tokens}
                />
              </>
            )}
          </div>
        )}

        {/* Compact Mode Toggle */}
        {showCompactToggle && (
          <CompactToggle
            isCompact={compactMode}
            onToggle={handleCompactToggle}
            tokens={tokens}
          />
        )}
      </div>
    </header>
  );
});

export default AppBar;
